import { useState } from 'react';

/**
 * The illustration that opens a chapter.
 *
 * A painting, one per chapter, showing the scene the chapter is about — the
 * figures, the animals, the place. That is the whole requirement, and it is why
 * these are images rather than the vector plates that came before them: faces
 * and bodies are exactly what hand-drawn SVG is worst at, and a chapter about
 * two people in a garden needs two people in a garden.
 *
 * ── The fallback, and why it is not in the bundle ───────────────────────────
 *
 * The vector plates still exist, as the safety net. A checkout that has not run
 * `npm run fetch:art` has no images in public/, and without a fallback the
 * Story section would open on twenty-eight broken-image icons — the worst
 * possible failure on the one screen meant to be the prettiest.
 *
 * But importing them statically put all twenty-eight drawings into the main
 * bundle: 350KB of JavaScript, downloaded by every visitor on every visit, for
 * a case that now only happens on a fresh clone. So the import moved inside the
 * error handler. Nobody pays for the safety net until they need it, and the
 * person who needs it is a developer on a fast connection rather than someone
 * on a phone.
 *
 * The failure is handled in three states rather than two, because `await
 * import()` is not instant: 'ok' while the painting is expected to work,
 * 'loading' between the image failing and the drawing arriving, and the drawing
 * itself. Without the middle state the frame collapses for a moment and the
 * page jumps.
 *
 * Loading is lazy and the box reserves its aspect ratio, so a chapter opens at
 * its final height and nothing moves when the painting arrives.
 */
export default function StoryPlate({ chapter, className = '' }) {
  // null = the painting is fine. A component = draw that instead. 'loading' =
  // the painting failed and the drawing is on its way.
  const [fallback, setFallback] = useState(null);

  const onImageError = async () => {
    if (fallback) return;
    setFallback('loading');
    try {
      const { PLATES } = await import('../art/story/index.js');
      setFallback(() => PLATES[chapter.id] ?? null);
    } catch {
      // Both the painting and the drawing are unreachable — almost certainly
      // offline. An empty frame is the honest answer, and the caption below
      // still says what the picture would have shown.
      setFallback(null);
    }
  };

  const Drawing = typeof fallback === 'function' ? fallback : null;

  return (
    <figure className={`plate ${className}`.trim()}>
      <div className="plate__frame">
        {Drawing ? (
          <svg
            className="plate__art"
            viewBox="0 0 320 200"
            role="img"
            aria-hidden="true"
            focusable="false"
          >
            <Drawing />
          </svg>
        ) : fallback === 'loading' ? null : (
          <img
            className="plate__painting"
            src={`/art/story/${chapter.id}.webp`}
            // The caption below repeats this, so the alt text carries it rather
            // than the chapter title, which the heading has already said.
            alt={chapter.artCaption ?? chapter.title}
            width="1264"
            height="848"
            loading="lazy"
            decoding="async"
            onError={onImageError}
          />
        )}
      </div>
      {chapter.artCaption ? (
        <figcaption className="plate__caption">{chapter.artCaption}</figcaption>
      ) : null}
    </figure>
  );
}
